



























































import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import DrawerModal from '@shared/components/modals/drawer-modal/drawer-modal.vue';
import vSelect from 'vue-select';
import EntitySelect from '@shared/components/forms/inputs/entity-select/entity-select.vue';
import TextEditor from '@shared/components/forms/inputs/text-editor/text-editor.vue';
import Input from '@shared/components/forms/inputs/input/input.vue';
import { ValidationObserver } from 'vee-validate';
import { Template } from '@tools/interfaces/template';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import { setProp } from '@shared/helpers/operators';
import { ActivitySessionAttendee } from '@activities/interfaces/activity-session-attendee';

interface FormModel {
  title: string;
  body: string;
  attendees: number[];
  template: Template;
}
@Component({
  name: 'bpc-notify-summons-attendee-modal',
  components: {
    'bpc-drawer-modal': DrawerModal,
    'v-select': vSelect,
    'bpc-entity-select': EntitySelect,
    'bpc-text-editor': TextEditor,
    'bpc-input': Input,
    ValidationObserver,
  },
})
export default class NotifySummonsAttendeeModal extends Vue {
  // PROPS
  @Prop([Number, String]) readonly sessionId: number | string;
  @Prop({ type: Boolean, default: false }) readonly show: boolean;
  @Prop({ type: String, default: 'notify' }) readonly action: string;
  @Prop({ type: String, default: null }) readonly selectableCondition: string;
  @Prop({
    type: Array,
    default() {
      return [];
    },
  })
  readonly defaultAttendeeIds: number[];

  // DATA
  loading: boolean = false;
  formModel: FormModel = {
    attendees: null,
    template: null,
    body: null,
    title: null,
  };
  templates: Template[] = [];
  attendees: ActivitySessionAttendee[] = [];

  // COMPUTED
  get drawerTitle() {
    return this.action === 'summon'
      ? this.$t('global.modal.summon.title')
      : this.$t('global.messages_entity.send_message.label');
  }

  // Only for summon action
  get defaultSummonsTemplateId() {
    return this.$store.getters['centerSettings/centerSettings'].summons_email_template_id;
  }

  get canBeSent() {
    return (
      !!this.formModel?.title && !!this.formModel?.body && this.formModel?.attendees?.length > 0
    );
  }

  // LIFECYCLE
  mounted() {
    this.getTemplates();
    this.getAttendees();
  }

  // METHODS
  getTemplates() {
    const queryConfig = {
      params: {
        entity: 'activity_session_attendee',
        available_in: 'admin',
        type: 'email',
        per_page: -1,
      },
    };
    this.$bpcApi.get(this.$bpcApiRouteHelper.getEntityPath('template'), queryConfig).subscribe(
      (res: BPCGenericApiResponse) => {
        this.templates = res.data;

        // Set the default template if exists and the action if for summon players
        if (this.action === 'summon') {
          this.setDefaultTemplate();
        }
      },
      error => {
        console.log(error);
      },
    );
  }

  // Only for summon action
  setDefaultTemplate() {
    if (this.defaultSummonsTemplateId) {
      const defaultTemplate = this.templates.find(
        item => item.id === this.defaultSummonsTemplateId,
      );
      if (defaultTemplate) {
        setProp(this.formModel, 'template', defaultTemplate);
        this.setTitleWithTemplateSelectedContent();
        this.setBodyWithTemplateSelectedContent();
      }
    }
  }

  getAttendees() {
    const queryConfig = {
      params: {
        activity_session_id: this.sessionId,
        per_page: -1,
      },
    };
    this.$bpcApi
      .get(this.$bpcApiRouteHelper.getEntityPath('activity-session-attendee'), queryConfig)
      .subscribe(
        (res: BPCGenericApiResponse) => {
          this.attendees = res.data;

          // Select by default all selectable ones if the action is summon
          this.selectAttendeesByDefault();
        },
        error => {
          console.log(error);
        },
      );
  }

  selectAttendeesByDefault() {
    if (this.action === 'summon') {
      const selectableAttendees = this.attendees.filter(item => !item.summoned);
      if (selectableAttendees) {
        setProp(this.formModel, 'attendees', selectableAttendees);
      }
    } else {
      const selectableAttendees = this.attendees.filter(item =>
        this.defaultAttendeeIds.includes(item.id),
      );
      if (selectableAttendees) {
        setProp(this.formModel, 'attendees', selectableAttendees);
      }
    }
  }

  closeDrawer() {
    this.$emit('close');
  }

  onNotifySummons() {
    if (this.action === 'summon') {
      this.sendSummons();
    } else {
      this.sendMessage();
    }
  }

  sendSummons() {
    this.loading = true;
    const url =
      this.$bpcApiRouteHelper.getEntityPath('activity-session-attendee') + '/send-summons';
    const data = {
      template_id: this.formModel?.template?.id,
      attendee_ids: this.formModel?.attendees?.map(item => {
        return item['id'];
      }),
      title: this.formModel?.title,
      body: this.formModel?.body,
    };

    this.$bpcApi.post(url, data).subscribe(
      (res: BPCGenericApiResponse) => {
        this.$emit('summons-sent');
        this.closeDrawer();
        this.loading = false;
      },
      error => {
        console.log(error);
        this.loading = false;
      },
    );
  }

  sendMessage() {
    this.loading = true;
    const url = this.$bpcApiRouteHelper.getEntityPath('message');
    const data = {
      entity_name: 'activity-session-attendee',
      entity_ids: this.formModel?.attendees?.map(item => {
        return item['id'];
      }),
      title: this.formModel?.title,
      body: this.formModel?.body,
      sending_type: 'personalized',
    };

    this.$bpcApi.post(url, data).subscribe(
      (res: BPCGenericApiResponse) => {
        this.closeDrawer();
        this.loading = false;
      },
      error => {
        console.log(error);
        this.loading = false;
      },
    );
  }

  setTitleWithTemplateSelectedContent() {
    this.formModel.title = this.formModel?.template?.name || '';
  }

  setBodyWithTemplateSelectedContent() {
    this.formModel.body = this.formModel?.template?.content || '';
  }

  onChangeTemplate(val) {
    if (val) {
      this.setTitleWithTemplateSelectedContent();
      this.setBodyWithTemplateSelectedContent();
    }
  }

  checkConditionToBeSelectable(option) {
    if (this.action === 'summon') {
      return !option.summoned;
    } else {
      switch (this.selectableCondition) {
        case 'confirmed':
          return option.summoned && option.coming === 'yes';
        case 'missing':
          return option.summoned && option.coming === 'no';
        case 'waiting':
          return option.summoned && option.coming === null;
        case 'has_vehicle':
          return option.summoned && option.trip === 'has_vehicle';
        case 'need_vehicle':
          return option.summoned && option.trip === 'need_vehicle';
        case 'going_directly':
          return option.summoned && option.trip === 'going_directly';
        default:
          return option.summoned;
      }
    }
  }

  // WATCHERS
}
